import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  ScriptReviewPlayer,
  type PlayerNoteTarget,
} from "@/features/script-review/components/ScriptReviewPlayer";
import {
  scriptReviewShareQueryOptions,
  postGuestNote,
} from "@/lib/queries/script-review-share.queries";
import { useStableVideoUrl } from "@/features/script-review/lib/use-stable-video-url";
import {
  trackScriptReviewGuestPageViewed,
  trackScriptReviewGuestNotePosted,
} from "@/lib/analytics-events";

// Public guest route — OUTSIDE _authenticated, no beforeLoad guard.
export const Route = createFileRoute("/sr/$token")({
  component: GuestReviewPage,
});

function GuestReviewPage() {
  const { t } = useTranslation();
  const { token } = Route.useParams();
  const qc = useQueryClient();
  const options = scriptReviewShareQueryOptions(token);
  const query = useQuery(options);
  const data = query.data;
  // The share payload re-signs the video URL on every fetch, and this query
  // refetches after each posted note — pin it so replying never reloads the
  // video or bounces the reviewer out of their place.
  const videoUrl = useStableVideoUrl(data?.id, data?.videoUrl);

  // Anonymous guest page view — fire once per loaded review. The query is
  // invalidated after every posted note (staleTime 0), so the ref keyed by the
  // review id (not the token — a capability secret we never track) guards it.
  const viewTrackedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!data || viewTrackedRef.current === data.id) return;
    viewTrackedRef.current = data.id;
    trackScriptReviewGuestPageViewed({
      review_id: data.id,
      comment_count: data.comments.length,
      note_count: data.notes.length,
    });
  }, [data]);

  // The token is a capability URL — don't leak it via the Referer header.
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "referrer";
    meta.content = "no-referrer";
    document.head.appendChild(meta);
    document.title = "Video review · Adge AI";
    return () => {
      document.head.removeChild(meta);
      document.title = "Adge AI";
    };
  }, []);

  const onPostNote = async (name: string, body: string, target: PlayerNoteTarget) => {
    try {
      await postGuestNote(token, { name, body, ...target });
      await qc.invalidateQueries({ queryKey: options.queryKey });
      if (data) {
        trackScriptReviewGuestNotePosted({
          review_id: data.id,
          note_kind: "aiCommentId" in target ? "reply" : "standalone",
          body_length: body.length,
        });
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not post the comment");
      throw e;
    }
  };

  return (
    <div className="min-h-dvh bg-background text-foreground antialiased">
      <header className="border-b border-border">
        <div className="mx-auto flex h-14 w-full max-w-6xl items-center gap-3 px-5">
          <span className="text-sm font-semibold tracking-tight">Adge AI</span>
          <span className="h-3.5 w-px bg-border" />
          <span className="text-[13px] text-muted-foreground">Video review</span>
        </div>
      </header>

      <main className="mx-auto w-full max-w-6xl px-5 py-8">
        {query.isLoading && <p className="text-sm text-muted-foreground">Loading review…</p>}

        {query.isError && (
          <div className="flex flex-col items-center gap-3 py-24 text-center">
            <p className="text-lg font-medium">This link is no longer available.</p>
            <p className="max-w-sm text-sm text-muted-foreground">
              The share link may have been revoked. Ask the sender for a fresh link.
            </p>
          </div>
        )}

        {data && (
          <>
            {/* Guest page intentionally uses plain copy (public, outside i18n). */}
            <div className="mb-4 flex items-center gap-2">
              <h1 className="text-lg font-semibold tracking-tight">Video review</h1>
              {data.version > 1 && (
                <span className="rounded bg-slate-100 px-1.5 py-0.5 text-xs font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                  {t("scriptReview.version.badge", "v{{n}}", { n: data.version })}
                </span>
              )}
            </div>
            <ScriptReviewPlayer
              videoUrl={videoUrl}
              aiComments={data.comments}
              notes={data.notes}
              failedPasses={data.failedPasses}
              canComment
              allowStandaloneNotes
              onPostNote={onPostNote}
            />
          </>
        )}
      </main>
    </div>
  );
}
